/**
 * Phase 16 Plan 16-03 — UnlinkMachinesConfirmDialog
 *
 * Destructive AlertDialog confirming removal of machine(s) from a Work Order.
 * On 409 (logsheet entries reference the WO+machine pair), surfaces backend error inline.
 */

import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRemoveMachinesMutation } from '@/hooks/useWorkOrderQueries';
import { toast } from '@/lib/toast';
import type { WorkOrderDetail } from '@/types/work-orders';

export interface UnlinkMachinesConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (o: boolean) => void;
  readonly workOrder: WorkOrderDetail;
  /** Machine IDs to unlink. */
  readonly machineIds: readonly number[];
  /** Human-readable machine code(s) for the confirmation message. */
  readonly machineLabels: readonly string[];
}

export function UnlinkMachinesConfirmDialog({
  open,
  onOpenChange,
  workOrder,
  machineIds,
  machineLabels,
}: UnlinkMachinesConfirmDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mutation = useRemoveMachinesMutation();

  function handleClose(): void {
    setErrorMessage(null);
    mutation.reset();
    onOpenChange(false);
  }

  function handleConfirm(): void {
    setErrorMessage(null);
    mutation.mutate(
      { id: workOrder.id, body: { machineIds } },
      {
        onSuccess: () => {
          toast.success(
            `${String(machineIds.length)} machine(s) unlinked from ${workOrder.woNumber}`,
          );
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setErrorMessage(msg);
          toast.error('Unlink failed', { description: msg });
        },
      },
    );
  }

  const machineCount = machineIds.length;
  const labelText =
    machineLabels.length > 0 ? machineLabels.join(', ') : `${String(machineCount)} machine(s)`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="unlink-machines-dialog">
        <DialogHeader>
          <DialogTitle>Unlink {String(machineCount)} machine{machineCount !== 1 ? 's' : ''}?</DialogTitle>
          <DialogDescription>
            Unlink <strong>{labelText}</strong> from <strong>{workOrder.woNumber}</strong>?
            Existing logsheet entries that reference this (WO, machine) pair will block removal
            with a 409 error.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          {errorMessage !== null && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="unlink-machines-dialog-error"
            >
              {errorMessage}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
            data-testid="unlink-machines-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={mutation.isPending}
            data-testid="unlink-machines-dialog-confirm"
          >
            {mutation.isPending ? 'Unlinking…' : 'Unlink'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
